'use client'

import {
  ChangeEvent,
  useId,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import { InputProps } from './types'

export function Input({
  id,
  label,
  value,
  defaultValue = '',
  className = '',
  required,
  disabled,
  onChange,
  onClear,
  onValueChange,
  ...props
}: InputProps) {
  const generatedId = useId()
  const fieldId = id ?? `input-${generatedId}`
  const inputRef = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const currentValue = value ?? internalValue

  const hasValue = currentValue.length > 0
  const isInvalid =
    props['aria-invalid'] === true || props['aria-invalid'] === 'true'
  const stateClasses = isInvalid
    ? 'border-[#9C0B0B] focus-within:border-[#9C0B0B] focus-within:ring-[#9C0B0B]'
    : 'focus-within:border-[#F1BD03] focus-within:ring-lightning-yellow-600'
  const valueClasses =
    hasValue && !isInvalid
      ? 'border-[#F1BD03] shadow-[0_0_8px_#E8FF737A]'
      : ''
  const emptyClasses = !hasValue && !isInvalid ? 'border-transparent' : ''

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const nextValue = event.currentTarget.value

    if (value === undefined) {
      setInternalValue(nextValue)
    }

    onValueChange?.(nextValue)
    onChange?.(event)
  }

  function handleClear() {
    const input = inputRef.current

    if (value === undefined) {
      setInternalValue('')
    }

    onValueChange?.('')
    onClear?.()

    if (input && onChange) {
      input.value = ''
      onChange({
        currentTarget: input,
        target: input,
      } as ChangeEvent<HTMLInputElement>)
    }

    input?.focus()
  }

  return (
    <div className={`w-full min-w-0 text-left ${className}`}>
      <div
        className={`relative h-12 w-full rounded-xl border bg-white-50 text-white-950 transition-shadow focus-within:ring-2 focus-within:ring-offset-2 ${stateClasses} ${valueClasses} ${emptyClasses} ${
          disabled ? 'opacity-70' : ''
        }`}
      >
        <label
          htmlFor={fieldId}
          className={`pointer-events-none absolute left-5 right-14 truncate text-left font-normal transition-all ${
            hasValue
              ? 'top-2 text-xs leading-3'
              : 'top-1/2 -translate-y-1/2 text-base leading-5'
          }`}
        >
          {label}
          {required ? (
            <span aria-hidden="true" className="text-[#CA0E0E]">
              {' '}
              *
            </span>
          ) : null}
        </label>
        <input
          ref={inputRef}
          id={fieldId}
          value={currentValue}
          required={required}
          aria-required={required || undefined}
          disabled={disabled}
          className={`h-full w-full rounded-xl bg-transparent px-5 pr-14 font-sans text-sm leading-5 text-white-950 outline-none placeholder:text-transparent ${
            hasValue ? 'pb-2 pt-5' : 'py-3'
          }`}
          onChange={handleChange}
          {...props}
        />
        {hasValue && !disabled ? (
          <button
            type="button"
            className="absolute right-1 top-1/2 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-white-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
            aria-label={`Limpar ${label}`}
            onClick={handleClear}
          >
            <span className="flex size-6 items-center justify-center rounded-full bg-lightning-yellow-500">
              <Image
                src="/assets/icons/x.svg"
                alt=""
                width={16}
                height={16}
                aria-hidden="true"
                className="size-4"
              />
            </span>
          </button>
        ) : null}
      </div>
    </div>
  )
}
